/**
 * Centre column: the volume being read, with whatever masks are laid over
 * it. Files are picked in the right column (or the base dropdown here); this
 * column only draws them, as 2D slices or through NiiVue, plus a short log.
 */

import * as React from "react";
import { Box, ChevronDown, ChevronUp, Scan, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { overlayColor } from "@/viewer/BaseOverlayPicker";
import { SliceCanvas } from "@/viewer/SliceCanvas";
import type { SharedVolumeFile } from "@/viewer/SharedVolumeViewer";
import { useResolvedVolumeUrls } from "@/viewer/useResolvedVolumeUrls";
import { useLogLines } from "@/store/appStore";
import { NiivueView, overlayColormap } from "./NiivueView";
import { resolveUrl, stripExt, useAllFiles, useSimple } from "./store";

type Mode = "slices" | "niivue";

function rgb([r, g, b]: [number, number, number]) {
  return `rgb(${r}, ${g}, ${b})`;
}

function LogStrip() {
  const lines = useLogLines();
  const [open, setOpen] = React.useState(false);
  const endRef = React.useRef<HTMLDivElement | null>(null);
  const last = lines[lines.length - 1];

  React.useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ block: "end" });
  }, [open, lines.length]);

  return (
    <div className="shrink-0 border-t border-ads-border bg-ads-surface">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[11px] text-ads-muted hover:bg-ads-hover"
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
        <span className="font-medium text-ads-text">Log</span>
        <span className="min-w-0 flex-1 truncate font-mono">{last ? last.line : "Pipeline output appears here while a run is going."}</span>
      </button>
      {open ? (
        <div className="h-48 overflow-y-auto border-t border-ads-border px-3 py-1 font-mono text-[11px] leading-relaxed text-neutral-700">
          {lines.map((l) => (
            <div
              key={l.seq}
              className={cn(l.level === "error" && "text-red-600", l.level === "warn" && "text-amber-700")}
            >
              {l.line}
            </div>
          ))}
          <div ref={endRef} />
        </div>
      ) : null}
    </div>
  );
}

export function ViewerPanel() {
  const all = useAllFiles();
  const baseId = useSimple((s) => s.baseId);
  const overlayIds = useSimple((s) => s.overlayIds);
  const [mode, setMode] = React.useState<Mode>("slices");

  const volumes = React.useMemo(() => all.filter((f) => f.isVolume), [all]);
  const files = React.useMemo<SharedVolumeFile[]>(
    () => volumes.map((f) => ({ id: f.id, label: stripExt(f.name), resolve: () => resolveUrl(f.id) })),
    [volumes],
  );
  const { baseUrl, overlays } = useResolvedVolumeUrls(files, baseId, overlayIds);
  const byId = React.useMemo(() => new Map(volumes.map((f) => [f.id, f])), [volumes]);

  return (
    <div className="flex h-full min-h-0 flex-col bg-ads-viewer">
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-ads-border bg-ads-surface px-3 py-2">
        <select
          value={baseId ?? ""}
          onChange={(e) => useSimple.getState().setBase(e.target.value || null)}
          className="h-7 min-w-0 max-w-[320px] flex-1 rounded border border-ads-border-strong bg-ads-surface px-2 text-[12px]"
        >
          <option value="">Choose an image…</option>
          {volumes.map((f) => (
            <option key={f.id} value={f.id}>
              {f.run ? `run ${f.run} · ` : "input · "}
              {stripExt(f.name)}
            </option>
          ))}
        </select>
        <select
          value=""
          disabled={!baseId}
          onChange={(e) => e.target.value && useSimple.getState().toggleOverlay(e.target.value)}
          className="h-7 max-w-[200px] rounded border border-ads-border-strong bg-ads-surface px-2 text-[12px] text-ads-muted disabled:opacity-40"
          title="Lay another image or mask over the base"
        >
          <option value="">+ Overlay…</option>
          {volumes
            .filter((f) => f.id !== baseId && !overlayIds.includes(f.id))
            .sort((a, b) => Number(b.isMask) - Number(a.isMask))
            .map((f) => (
              <option key={f.id} value={f.id}>
                {f.isMask ? "◆ " : ""}
                {f.run ? `run ${f.run} · ` : "input · "}
                {stripExt(f.name)}
              </option>
            ))}
        </select>
        {overlayIds.map((id, i) => (
          <span
            key={id}
            className="flex items-center gap-1 rounded-full border border-ads-border px-2 py-0.5 text-[11px] text-ads-text"
          >
            <span className="h-2 w-2 rounded-full" style={{ background: rgb(overlayColor(i)) }} />
            {stripExt(byId.get(id)?.name ?? id)}
            <button
              type="button"
              aria-label="Remove overlay"
              onClick={() => useSimple.getState().toggleOverlay(id)}
              className="text-ads-faint hover:text-ads-text"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <div className="ml-auto flex items-center gap-0.5">
          {(
            [
              ["slices", Scan, "Slices"],
              ["niivue", Box, "NiiVue"],
            ] as const
          ).map(([m, Icon, label]) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={cn(
                "flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium transition",
                mode === m ? "bg-ads-accent-soft text-ads-accent" : "text-neutral-500 hover:bg-ads-hover",
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="relative min-h-0 flex-1">
        {!baseId ? (
          <div className="flex h-full items-center justify-center text-[12.5px] text-neutral-400">
            Open a subject on the left, or click an image in Outputs.
          </div>
        ) : mode === "slices" ? (
          <SliceCanvas baseUrl={baseUrl} overlays={overlays} />
        ) : baseUrl ? (
          <NiivueView
            base={{ url: baseUrl, name: byId.get(baseId)?.name ?? "base.nii.gz", colormap: "gray" }}
            overlays={overlays.map((o, i) => ({
              url: o.url,
              name: byId.get(overlayIds[i])?.name ?? `overlay${i}.nii.gz`,
              colormap: overlayColormap(i),
            }))}
          />
        ) : null}
      </div>

      <LogStrip />
    </div>
  );
}
